import React, { useCallback } from 'react'

import styled from 'styled-components'

import Select from '@mui/material/Select'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Checkbox from '@mui/material/Checkbox'

import Table from 'components/Table'

import { RoleFeature } from 'types/RoleFeature'

type Props = {
  onUpdateRoleFeatureCallback: (old: RoleFeature, updated: RoleFeature) => void
  roleFeature: RoleFeature
  disabled: boolean
}

const ROLES = ['dealer', 'fsr', 'technician', 'admin']

const EditRoleFeature = ({ roleFeature, onUpdateRoleFeatureCallback, disabled }: Props) => {
  const onRolesUpdated = useCallback((value: string | Array<string>) => {
    // On autofill we get a stringified value.
    const roles = typeof value === 'string' ? value.split(',') : value

    onUpdateRoleFeatureCallback(roleFeature, { ...roleFeature, roles })
  }, [onUpdateRoleFeatureCallback, roleFeature])

  return (
    <Table.Row role="row" key={roleFeature.featureCode}>
      <S.Cell><S.Code>{roleFeature.featureCode}</S.Code></S.Cell>
      <S.Cell>
        <S.RolesSelect
          multiple
          displayEmpty
          onChange={(event) => onRolesUpdated(event.target.value)}
          value={roleFeature.roles}
          input={<OutlinedInput />}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <S.DefaultText>Select Roles</S.DefaultText>
            }

            return selected.join(', ')
          }}
          inputProps={{ 'aria-label': 'Without label' }}
          disabled={disabled}
        >
          <MenuItem disabled value="">
            <S.DefaultText>Select Roles</S.DefaultText>
          </MenuItem>

          {ROLES.map((role) => (
            <MenuItem key={role} value={role}>
              <Checkbox checked={roleFeature.roles.includes(role)} />
              <S.ListItemText primary={role} />
            </MenuItem>
          ))}
        </S.RolesSelect>
      </S.Cell>
    </Table.Row>
  )
}

export default EditRoleFeature

const S = {
  Cell: styled(Table.Cell)`
    padding: 2px 2px 5px 10px;
    align-items: center;
  `,
  Code: styled.span`
    font-size: 1.25rem;
  `,
  DefaultText: styled.em`
    text-transform: capitalize;
  `,
  RolesSelect: styled(Select<Array<string>>)`
    min-width: 300px;
    max-height: 40px;
    padding-left: 10px;
    text-transform: uppercase;
  `,
  ListItemText: styled(ListItemText)`
    text-transform: uppercase;
  `
}
